<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useI18n } from '#imports';
import imgHero1 from '~/assets/images/hero01.jpg';

const { t } = useI18n();

const slides = [
  imgHero1,
  'https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=1440',
  'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=1440',
];

const currentIndex = ref(0);

// Track load states
const loadedSlides = ref<Record<string, boolean>>({});
const imageErrors = ref<Record<string, boolean>>({});

const onImageLoad = (slide: string) => {
  loadedSlides.value[slide] = true;
};

const onImageError = (slide: string) => {
  imageErrors.value[slide] = true;
};

// ─── Slide timer ──────────────────────────────────────────────────────────────

let timer: ReturnType<typeof setInterval> | null = null;

const startTimer = () => {
  if (timer) clearInterval(timer);
  timer = setInterval(() => {
    currentIndex.value = (currentIndex.value + 1) % slides.length;
  }, 5000);
};

const stopTimer = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};

// ─── Reusable auto-play pause/resume with swipe threshold ────────────────────

const isDragging = ref(false);

const { touchHandlers, mouseHandlers } = useCarouselAutoPlay(startTimer, stopTimer, {
  resumeDelay: 3000,
  isDragging,
});

// ─── Dot navigation ──────────────────────────────────────────────────────────

const setSlide = (index: number) => {
  currentIndex.value = index;
  stopTimer();
  startTimer();
};

// ─── Typing animation ────────────────────────────────────────────────────────

const fullText = computed(() => t('hero.title'));
const typedText = ref('');
let typingInterval: ReturnType<typeof setInterval> | null = null;

const startTyping = () => {
  if (!import.meta.client) return;
  if (typingInterval) {
    clearInterval(typingInterval);
  }
  typedText.value = '';
  let index = 0;
  const textToType = fullText.value;

  typingInterval = setInterval(() => {
    if (index < textToType.length) {
      typedText.value += textToType.charAt(index);
      index++;
    } else {
      if (typingInterval) {
        clearInterval(typingInterval);
        typingInterval = null;
      }
    }
  }, 75); // 75ms per character for an active, engaging typing speed
};

watch(
  fullText,
  () => {
    startTyping();
  },
  { immediate: true }
);

onMounted(() => {
  startTimer();
  startTyping();
});

onUnmounted(() => {
  stopTimer();
  if (typingInterval) {
    clearInterval(typingInterval);
  }
});
</script>

<template>
  <section
    class="group w-full bg-white"
    @mouseenter="mouseHandlers.onEnter"
    @mouseleave="mouseHandlers.onLeave"
    v-on="touchHandlers"
  >
    <div class="relative mx-auto w-full">
      <!-- Slides -->
      <div class="relative h-[65vh] min-h-[380px] w-full overflow-hidden bg-slate-900 md:h-[520px] lg:h-[700px]">
        <transition-group name="fade" tag="div" class="relative h-full w-full">
          <div
            v-for="(slide, index) in slides"
            v-show="currentIndex === index"
            :key="slide"
            class="absolute inset-0 h-full w-full"
          >
            <!-- Foreground image — covers full 6:9 container -->
            <img
              :src="slide"
              alt="E-Learning — E-SCHOOL Cambodia"
              draggable="false"
              class="absolute inset-0 h-full w-full object-cover object-center"
              @load="onImageLoad(slide)"
              @error="onImageError(slide)"
            />

            <!-- Loading indicator -->
            <Transition name="fade-loader">
              <LogoLoader
                v-if="!loadedSlides[slide] && !imageErrors[slide]"
                size="h-16 w-16"
                text="Loading..."
                overlay-class="bg-slate-900/90"
              />
            </Transition>

            <!-- Subtle dark overlay to match premium aesthetic -->
            <div class="absolute inset-0 bg-black/40"></div>
          </div>
        </transition-group>

        <!-- Static Hero content overlay -->
        <div class="absolute inset-0 mx-auto max-w-[1440px]">
          <div
            class="absolute inset-0 top-12 z-10 flex max-w-4xl flex-col items-start justify-center px-8 text-left text-white sm:left-2 md:px-20 lg:left-10"
          >
            <h1
              class="min-h-[4rem] font-sans text-2xl leading-[1.3] font-extrabold drop-shadow-md md:min-h-[6rem] md:text-4xl md:leading-[1.3] lg:min-h-[8rem] lg:text-5xl lg:leading-[1.3]"
            >
              <span class="animated-gradient-text">{{ typedText }}</span
              ><span class="hero-typing-cursor">|</span>
            </h1>
            <p
              class="mt-4 max-w-2xl text-xs leading-relaxed font-medium text-white/90 drop-shadow-sm md:text-base lg:text-lg"
            >
              {{ t('hero.subtitle') }}
            </p>

            <!-- Action Buttons -->
            <div class="mt-8 flex flex-wrap justify-start gap-4">
              <NuxtLink
                to="/download"
                class="btn-3d btn-hero-primary inline-flex items-center justify-center rounded-full bg-brand-blue px-6 py-3 text-xs font-bold text-white md:px-8 md:py-3.5 md:text-sm"
                style="--btn-3d-shadow: rgba(0, 0, 0, 0.35)"
              >
                {{ t('hero.startLearning') }}
              </NuxtLink>
              <NuxtLink
                to="/courses"
                class="btn-3d-glass btn-hero-secondary inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-6 py-3 text-xs font-bold text-white backdrop-blur-md md:px-8 md:py-3.5 md:text-sm"
              >
                {{ t('hero.liveClass') }}
              </NuxtLink>
            </div>
          </div>
          <!-- Left/Right navigation arrows (premium touch) -->
          <button
            class="absolute top-1/2 left-4 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/20 text-white opacity-0 backdrop-blur-xs transition duration-300 group-hover:opacity-100 hover:bg-black/40"
            aria-label="Previous slide"
            @click="currentIndex = (currentIndex - 1 + slides.length) % slides.length"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2.5"
              stroke="currentColor"
              class="h-5 w-5"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            class="absolute top-1/2 right-4 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/20 text-white opacity-0 backdrop-blur-xs transition duration-300 group-hover:opacity-100 hover:bg-black/40"
            aria-label="Next slide"
            @click="currentIndex = (currentIndex + 1) % slides.length"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="2.5"
              stroke="currentColor"
              class="h-5 w-5"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Navigation dots -->
      <div class="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-3">
        <button
          v-for="(_, index) in slides"
          :key="index"
          class="h-2.5 w-2.5 rounded-full transition-all duration-300"
          :class="currentIndex === index ? 'w-6 bg-white shadow-md' : 'bg-white/50 hover:bg-white/80'"
          :aria-label="`Go to slide ${index + 1}`"
          @click="setSlide(index)"
        ></button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-loader-enter-active,
.fade-loader-leave-active {
  transition: opacity 0.5s ease;
}

.fade-loader-enter-from,
.fade-loader-leave-to {
  opacity: 0;
}

.animated-gradient-text {
  background: linear-gradient(135deg, #00c6ff, #0072ff, #f953c6, #b91d73, #00c6ff);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradient-move 4s linear infinite;
  display: inline;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  padding: 0.15em 0;
}

@keyframes gradient-move {
  0% {
    background-position: 0% center;
  }
  100% {
    background-position: 200% center;
  }
}

.hero-typing-cursor {
  display: inline-block;
  margin-left: 2px;
  color: #00c6ff;
  animation: blink 0.8s infinite;
  font-weight: 300;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

/* Custom Aurora Flow Hover Animations */
.btn-hero-primary {
  position: relative;
  overflow: visible; /* Prevent clipping 3D tactile shadow */
  z-index: 1;
}

.btn-hero-primary::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #005174, #00c6ff, #f953c6, #005174);
  background-size: 300% 300%;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.4s ease;
  border-radius: inherit;
}

.btn-hero-primary:hover::before {
  opacity: 1;
  animation: aurora-move 4s ease infinite;
}

.btn-hero-secondary {
  position: relative;
  overflow: visible; /* Prevent clipping 3D tactile shadow */
  z-index: 1;
}

.btn-hero-secondary::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 81, 116, 0.4),
    rgba(0, 198, 255, 0.4),
    rgba(249, 83, 198, 0.4),
    rgba(0, 81, 116, 0.4)
  );
  background-size: 300% 300%;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.4s ease;
  border-radius: inherit;
}

.btn-hero-secondary:hover::before {
  opacity: 1;
  animation: aurora-move 4s ease infinite;
}

@keyframes aurora-move {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
